import { useReviewPeriodLock } from '../workspace/reviewPeriod';
import {useAbfCopilotContext, ExplainMetricButton} from '../copilot/AbfCopilotLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatAbfDate } from '../../../utils/dateUtils';
import styled from 'styled-components';
import {
  AlertTriangle,
  Archive,
  Calculator,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  FileCheck2,
  GitCompareArrows,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import {
  apiErrorMessage,
  compareBorrowingBaseToBbc,
  finalizeBorrowingBaseEvidencePack,
  getBorrowingBaseWorkspace,
} from '../api/abfApi';
import type {
  BorrowingBaseAction,
  BorrowingBaseRun,
  BorrowingBaseWorkspaceReadModel,
} from '../types/borrowingBase';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { PrerequisiteAction } from '../components/PrerequisiteAction';
import { facilityWorkspacePath } from '../routes';

const Shell = styled.div`
  display: grid;
  gap: 0.85rem;
  margin-top: 0.85rem;
`;

const Panel = styled.section`
  padding: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  background: rgba(8, 16, 30, 0.78);
`;

const Heading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.8rem;
  h2, h3 { margin: 0; color: #f8fafc; font-size: 0.9rem; }
  p { margin: 0.3rem 0 0; color: #94a3b8; font-size: 0.69rem; line-height: 1.45; }
`;

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
  @media (max-width: 850px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Fact = styled.div`
  min-width: 0;
  padding: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.5);
`;

const Label = styled.div`
  color: #64748b;
  font-size: 0.57rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Value = styled.div<{ $tone?: 'green' | 'amber' | 'red' }>`
  overflow: hidden;
  margin-top: 0.3rem;
  color: ${p => p.$tone === 'green' ? '#6ee7b7' : p.$tone === 'amber' ? '#fcd34d' : p.$tone === 'red' ? '#fb7185' : '#f8fafc'};
  font-size: 0.8rem;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** A checkbox beside its words, not a label-above field. */
const CheckField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #cbd5e1;
  font-size: 0.7rem;
  font-weight: 650;
  cursor: pointer;
  input { margin: 0; width: 14px; height: 14px; accent-color: #38bdf8; cursor: pointer; }
`;

const Sub = styled.div`
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.62rem;
  line-height: 1.4;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(18rem, 0.75fr);
  gap: 0.85rem;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;

const Table = styled.div`
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.13);
  border-radius: 10px;
`;

const Row = styled.div<{ $header?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 0.7fr);
  gap: 0.65rem;
  align-items: center;
  padding: 0.58rem 0.7rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  background: ${p => p.$header ? 'rgba(15, 23, 42, 0.82)' : 'transparent'};
  color: ${p => p.$header ? '#64748b' : '#cbd5e1'};
  font-size: ${p => p.$header ? '0.57rem' : '0.68rem'};
  font-weight: ${p => p.$header ? 850 : 650};
  text-transform: ${p => p.$header ? 'uppercase' : 'none'};
  > span { min-width: 0; overflow-wrap: anywhere; }
  &:last-child { border-bottom: 0; }
  @media (max-width: 760px) { grid-template-columns: 1.4fr 0.8fr; > :nth-child(3), > :nth-child(4) { display: none; } }
`;

const Explanation = styled.div`
  margin-top: 0.18rem;
  color: #64748b;
  font-size: 0.6rem;
  font-weight: 500;
`;

const RecordIdentity = styled.div`
  margin: 0 0 0.8rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(56, 189, 248, 0.2);
  border-radius: 9px;
  background: rgba(14, 116, 144, 0.08);
  color: #bae6fd;
  font-size: 0.67rem;
  line-height: 1.5;
`;

const EvidenceLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7dd3fc;
  cursor: pointer;
  font-size: 0.6rem;
  font-weight: 800;
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.7rem;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Contributor = styled.div`
  display: grid;
  grid-template-columns: minmax(8rem, 1.2fr) minmax(6rem, 0.8fr) minmax(5rem, 0.6fr);
  gap: 0.5rem;
  padding: 0.38rem 0;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  color: #cbd5e1;
  font-size: 0.61rem;
`;

const Status = styled.span<{ $tone?: 'green' | 'amber' | 'red' }>`
  display: inline-flex;
  width: fit-content;
  padding: 0.2rem 0.4rem;
  border: 1px solid ${p => p.$tone === 'green' ? 'rgba(52,211,153,.35)' : p.$tone === 'red' ? 'rgba(248,113,113,.4)' : 'rgba(251,191,36,.35)'};
  border-radius: 999px;
  color: ${p => p.$tone === 'green' ? '#6ee7b7' : p.$tone === 'red' ? '#fb7185' : '#fcd34d'};
  font-size: 0.58rem;
  font-weight: 850;
  text-transform: uppercase;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Field = styled.label`
  display: grid;
  gap: 0.3rem;
  color: #64748b;
  font-size: 0.57rem;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  input, select, textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0.58rem 0.65rem;
    border: 1px solid rgba(148, 163, 184, 0.22);
    border-radius: 9px;
    background: rgba(2, 6, 23, 0.58);
    color: #e2e8f0;
    font: inherit;
    font-size: 0.7rem;
    letter-spacing: normal;
    text-transform: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  flex-wrap: wrap;
  margin-top: 0.65rem;
`;

const WorkflowSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.45rem;
  @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

const WorkflowStep = styled.div<{ $state: 'complete' | 'current' | 'pending' }>`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  padding: 0.62rem 0.68rem;
  border: 1px solid ${p => p.$state === 'complete' ? 'rgba(52,211,153,.3)' : p.$state === 'current' ? 'rgba(56,189,248,.42)' : 'rgba(148,163,184,.14)'};
  border-radius: 9px;
  background: ${p => p.$state === 'complete' ? 'rgba(6,78,59,.18)' : p.$state === 'current' ? 'rgba(14,116,144,.16)' : 'rgba(15,23,42,.35)'};
  color: ${p => p.$state === 'complete' ? '#6ee7b7' : p.$state === 'current' ? '#bae6fd' : '#64748b'};
  font-size: 0.65rem;
  font-weight: 800;
`;

const NextAction = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  margin-top: 0.75rem;
  padding: 0.85rem;
  border: 1px solid rgba(56,189,248,.34);
  border-radius: 11px;
  background: rgba(14,116,144,.1);
  h3 { margin: 0; color: #e0f2fe; font-size: .8rem; }
  p { margin: .3rem 0 0; color: #a5c5d8; font-size: .67rem; line-height: 1.5; }
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const InlineReason = styled.div`
  margin-top: .45rem;
  color: #fcd34d;
  font-size: .63rem;
  line-height: 1.45;
`;


const Finding = styled.div`
  margin-top: 0.45rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid rgba(251, 191, 36, 0.24);
  border-radius: 9px;
  background: rgba(120, 53, 15, 0.08);
  color: #fcd34d;
  font-size: 0.66rem;
  line-height: 1.45;
`;

const Empty = styled.div`
  padding: 1.3rem;
  color: #94a3b8;
  text-align: center;
  font-size: 0.7rem;
`;

const shortHash = (value: unknown) => value ? `${String(value).slice(0, 12)}…` : 'Not available';
const display = (value: unknown, fallback = 'Not available') =>
  value === null || value === undefined || value === '' ? fallback : String(value).replace(/_/g, ' ');
const date = (value: unknown) => formatAbfDate(value);
const dateTime = (value: unknown) => {
  if (!value) return 'Not available';
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
  }).format(parsed);
};
const money = (value: unknown, currency: string) => value === null || value === undefined || value === ''
  ? 'Not available'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(value));
const signedMoney = (value: unknown, currency: string) => {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${money(number, currency)}`;
};
const actionReason = (action: BorrowingBaseAction, busy: boolean, extra?: string) =>
  busy ? 'Another borrowing-base action is in progress.' : extra || action.disabledReason || undefined;

interface Props {
  facilityId: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const BorrowingBaseWorkspace: React.FC<Props> = ({
  facilityId,
  currency = 'USD',
  onFeedback,
}) => {
  const ccy = currency || 'USD';
  const navigate = useNavigate();
  const [model, setModel] = useState<BorrowingBaseWorkspaceReadModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  useReviewPeriodLock(Boolean(busy));
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);
  const [submissionId, setSubmissionId] = useState('');
  const [overridePolicy, setOverridePolicy] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [tolerance, setTolerance] = useState('');
  const [materiality, setMateriality] = useState('');
  const [blocking, setBlocking] = useState('');

  const load = useCallback(async (notify = false) => {
    setLoading(true);
    try {
      const result = await getBorrowingBaseWorkspace(facilityId);
      setModel(result);
      setSubmissionId(current => current || String(result.bbc.compatibleSubmissions[0]?.id || ''));
      if (notify) {
        const feedback = { tone: 'success' as const, message: 'Borrowing-base authorities refreshed.' };
        setLocalFeedback(feedback);
        onFeedback(feedback);
      }
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Unable to load the borrowing-base workspace.') };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      setLoading(false);
    }
  }, [facilityId, onFeedback]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    setLocalFeedback({ tone: 'info', message: 'Recording the governed borrowing-base action…' });
    try {
      await action();
      const feedback = { tone: 'success' as const, message: success };
      setLocalFeedback(feedback);
      onFeedback(feedback);
      await load();
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'The borrowing-base action failed.') };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      setBusy('');
    }
  };

  const selectedSubmission = useMemo(() => model?.bbc.compatibleSubmissions.find(
    item => item.id === Number(submissionId),
  ), [model, submissionId]);

  const explanationRun = model?.candidateRun.id ? model.candidateRun : model?.approvedRun;
  useAbfCopilotContext({selectedRecord: explanationRun?.id ? {type: 'BORROWING_BASE_RUN', id: Number(explanationRun.id)} : undefined});

  if (loading && !model) return <Panel><Empty>Loading persisted borrowing-base authority…</Empty></Panel>;
  if (!model) return <Panel><Empty>Borrowing-base data is unavailable. Use the visible notification for details.</Empty></Panel>;

  const candidate = model.candidateRun;
  const approved = model.approvedRun;
  const activeVersion = model.currentContext.activeFacilityVersion;
  const currentRuleSet = model.currentContext.activeRuleSet;
  const latestTape = model.currentContext.latestReleasedTape;
  const historicalUnresolvedRuns = model.currentContext.historicalUnresolvedRuns;
  const approvedIsCurrent = Boolean(approved.id)
    && String(approved.facility_version_id) === String(activeVersion.id)
    && String(approved.tape_ingest_id) === String(latestTape.id)
    && String(approved.rule_set_id) === String(currentRuleSet.id);
  const run: BorrowingBaseRun = candidate.id ? candidate : approvedIsCurrent ? approved : {};
  const tape = model.authorityBundle.releasedTape;
  const version = model.authorityBundle.facilityVersion;
  const ruleSet = model.authorityBundle.ruleSet;
  const approvalState = display(run.approval_status);
  const varianceState = display(run.variance_status, 'Not compared');
  const hasCandidate = Boolean(candidate.id);
  const hasCurrentCalculation = Boolean(run.id);
  const hasComparison = hasCurrentCalculation && Boolean(run.variance_status)
    && String(run.variance_status).toUpperCase() !== 'PENDING';
  const acceptedVarianceResolutions = model.approvalControls.acceptedVarianceResolutions || [];
  const hasAcceptedBlockingVariance = Boolean(run.variance_blocking && acceptedVarianceResolutions.length);
  const compareExtra = !submissionId ? 'Select the same-period borrower BBC.'
    : overridePolicy && (!tolerance || !materiality || !blocking || !overrideReason.trim()) ? 'Enter all three override thresholds and a reason.'
      : undefined;
  const facilityName = display(model.facility.facility_name, 'this facility');
  // Decisions on the month (compute, approve, reject) are made in Period review (F-S2-UI-006).
  const openPeriodReview = () => navigate(`${facilityWorkspacePath(facilityId, 'collateral')}?collateralView=period`);
  const workflowSteps: Array<{ label: string; complete: boolean }> = [
    { label: 'Contract active', complete: Boolean(activeVersion.id) },
    { label: 'Tape released', complete: Boolean(latestTape.id) },
    { label: 'Compute', complete: hasCurrentCalculation },
    { label: 'Compare BBC', complete: hasComparison },
    { label: 'Approve', complete: approvedIsCurrent },
  ];
  const firstIncompleteStep = workflowSteps.findIndex(step => !step.complete);
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const nextAction = !activeVersion.id ? {
    title: 'Activate the contract authority',
    description: 'A verified active contract version is required before a governed borrowing base can be computed.',
    label: 'Open Contract',
    action: () => navigate(facilityWorkspacePath(facilityId, 'contract')),
    disabled: false,
    reason: undefined,
  } : !latestTape.id ? {
    title: 'Release a collateral tape',
    description: 'Receive, process, and release the collateral tape before computing borrowing-base authority.',
    label: 'Open Collateral',
    action: () => navigate(facilityWorkspacePath(facilityId, 'collateral')),
    disabled: false,
    reason: undefined,
  } : !hasCurrentCalculation ? {
    title: 'Compute the current governed borrowing base',
    description: historicalUnresolvedRuns.length
      ? `${historicalUnresolvedRuns.length} older pre-governance calculation${historicalUnresolvedRuns.length === 1 ? '' : 's'} will be retained in history and marked superseded automatically. No manual rejection is required.`
      : `Use released tape #${display(latestTape.id)} with Contract Version ${display(activeVersion.version_no)} and Rule Set v${display(currentRuleSet.version_no)}.`,
    label: 'Open Period review',
    action: openPeriodReview,
    disabled: false,
    reason: model.actions.recompute.allowed ? undefined : model.actions.recompute.disabledReason,
  } : hasCandidate && !hasComparison ? {
    title: 'Compare this candidate with the borrower BBC',
    description: `Run #${display(candidate.id)} is computed but cannot be approved until independent borrower-reported evidence is compared.`,
    label: 'Go to BBC comparison',
    action: () => scrollTo('bbc-comparison'),
    disabled: false,
    reason: undefined,
  } : hasCandidate ? {
    title: 'Decide the identified candidate in Period review',
    description: `Run #${display(candidate.id)} has comparison evidence. The approval or rejection is recorded in Period review, beside everything else the month still needs.`,
    label: 'Open Period review',
    action: openPeriodReview,
    disabled: false,
    reason: undefined,
  } : {
    title: 'Current borrowing-base authority is approved',
    description: `Run #${display(approved.id)} is the current approved authority. Finalize an evidence pack when the review record is complete.`,
    label: 'View approval record',
    action: () => scrollTo('approval-decision'),
    disabled: false,
    reason: undefined,
  };
  const openCollateralEvidence = (poolView: 'all' | 'eligible' | 'ineligible' | 'dq') => {
    const query = new URLSearchParams({ collateralView: 'explorer', poolView });
    if (tape.id) query.set('tape', String(tape.id));
    navigate(`${facilityWorkspacePath(facilityId, 'collateral')}?${query.toString()}`);
  };
  const bridgeAction = (key: string) => {
    switch (key) {
      case 'RELEASED_COLLATERAL': return { label: 'View released loans', action: () => openCollateralEvidence('all') };
      case 'DQ_EXCLUSIONS': return { label: 'View DQ evidence', action: () => openCollateralEvidence('dq') };
      case 'INELIGIBLE': return { label: 'View excluded loans and rule trace', action: () => openCollateralEvidence('ineligible') };
      case 'ELIGIBLE': return { label: 'View eligible loans and rule trace', action: () => openCollateralEvidence('eligible') };
      case 'GROSS_ADVANCE': return { label: 'View loan advance evidence', action: () => openCollateralEvidence('eligible') };
      case 'CONCENTRATION': return { label: 'View concentration evidence', action: () => document.getElementById('concentration-evidence')?.scrollIntoView({ behavior: 'smooth' }) };
      case 'RESERVES': return { label: 'View contract reserve source', action: () => navigate(facilityWorkspacePath(facilityId, 'contract')) };
      case 'COMPUTED_BB': return { label: 'View approval lineage', action: () => document.getElementById('approval-lineage')?.scrollIntoView({ behavior: 'smooth' }) };
      default: return null;
    }
  };

  return (
    <Shell>
      <Panel>
        <Heading>
          <div>
            <h2>Borrowing Base</h2>
            <p>One computed and approved authority, with borrower-reported evidence kept separate.</p>
          </div>
          <PrerequisiteAction disabled={loading || Boolean(busy)} disabledReason={loading || busy ? 'A workspace action is already in progress.' : undefined} onClick={() => load(true)}>
            <RefreshCw size={14} /> Refresh
          </PrerequisiteAction>
        </Heading>
        <WorkflowSteps>
          {workflowSteps.map((step, index) => (
            <WorkflowStep key={step.label} $state={step.complete ? 'complete' : index === firstIncompleteStep ? 'current' : 'pending'}>
              {step.complete ? <CheckCircle2 size={14} /> : <span>{index + 1}</span>}
              <span>{step.label}</span>
              {index < workflowSteps.length - 1 && <ChevronRight size={12} />}
            </WorkflowStep>
          ))}
        </WorkflowSteps>
        <NextAction>
          <div>
            <Label>Required next action</Label>
            <h3>{nextAction.title}</h3>
            <p>{nextAction.description}</p>
            {nextAction.reason && <InlineReason>Why this is unavailable: {nextAction.reason}</InlineReason>}
          </div>
          <PrerequisiteAction primary disabled={nextAction.disabled} disabledReason={nextAction.reason ?? undefined} onClick={nextAction.action}>
            {nextAction.label} <ChevronRight size={14} />
          </PrerequisiteAction>
        </NextAction>
        {historicalUnresolvedRuns.length > 0 && (
          <Finding><AlertTriangle size={13} style={{ marginRight: '.35rem', verticalAlign: 'text-bottom' }} />
            {historicalUnresolvedRuns.length} historical calculation{historicalUnresolvedRuns.length === 1 ? '' : 's'} do not match the active contract/rule lineage. They are not actionable and will be retained as superseded audit history when the current calculation is created.
          </Finding>
        )}
        <RecordIdentity>
          {hasCurrentCalculation ? <>
            <strong>{hasCandidate ? 'Current candidate' : 'Current approved authority'}: Run #{display(run.id)}</strong><br />
            Basis {date(run.as_of_date)} · Tape #{display(run.tape_ingest_id)} · Contract Version {display(activeVersion.version_no)} · Rule Set v{display(currentRuleSet.version_no)} · Status {display(run.approval_status)}<br />
            Created {dateTime(run.created_at)} by {display(run.created_by)}{run.approved_at ? ` · Decided ${dateTime(run.approved_at)} by ${display(run.approved_by)}` : ''}
          </> : <>
            <strong>No current governed calculation is awaiting action.</strong><br />
            The active lineage is Contract Version {display(activeVersion.version_no, '—')} · Rule Set v{display(currentRuleSet.version_no, '—')} · latest released Tape #{display(latestTape.id, '—')}.
          </>}
        </RecordIdentity>
        <FactGrid>
          <Fact><Label>Authority</Label><Value $tone={approvedIsCurrent ? 'green' : 'amber'}>{approvedIsCurrent ? 'Approved' : 'Unavailable'}</Value><Sub>{approvedIsCurrent ? `Run #${approved.id} · ${date(approved.as_of_date)}` : 'No current governed approval'}</Sub></Fact>
          <Fact><Label>Computed borrowing base</Label><Value>{money(run.computed_borrowing_base, ccy)}</Value><Sub>{approvalState}</Sub></Fact>
          <Fact><Label>Current availability</Label><Value $tone="green">{money(model.facility.excess_availability, ccy)}</Value><Sub>Approved base less current draws and reservations</Sub></Fact>
            <Fact><Label>BBC comparison</Label><Value $tone={hasAcceptedBlockingVariance ? 'amber' : run.variance_blocking ? 'red' : run.variance_status ? 'green' : 'amber'}>{varianceState}{hasAcceptedBlockingVariance ? ' · ACCEPTED' : ''}</Value><Sub>{hasAcceptedBlockingVariance ? 'Four-eyes disposition approved; original comparison retained' : run.variance_blocking ? 'Blocking' : 'No blocking variance'}</Sub></Fact>
        </FactGrid>
        <ActionLocalFeedback feedback={localFeedback} />
      </Panel>

      <Panel>
        <Heading><div><h3>Frozen authority bundle</h3><p>Every approval names the exact contract, released tape, rule set, reserve, engine and evidence fingerprints.</p></div><ShieldCheck size={18} color="#34d399" /></Heading>
        <FactGrid>
          <Fact><Label>Facility version</Label><Value>Version {display(version.version_no, '—')}</Value><Sub>Effective {date(version.effective_from)}</Sub></Fact>
          <Fact><Label>Released tape</Label><Value>{date(tape.as_of_date)}</Value><Sub>{display(tape.source_filename)}</Sub></Fact>
          <Fact><Label>Rules at basis date</Label><Value>Rule set v{display(ruleSet.version_no, '—')}</Value><Sub>{shortHash(model.authorityBundle.ruleVersionHash || ruleSet.definition_hash)}</Sub></Fact>
          <Fact><Label>Current governed rules</Label><Value>Rule set v{display(currentRuleSet.version_no, '—')}</Value><Sub>Effective {date(currentRuleSet.effective_from)}</Sub></Fact>
          <Fact><Label>Reserve <ExplainMetricButton scope="selection" metric="RESERVE" /></Label><Value>{money(model.authorityBundle.reserveAmount, ccy)}</Value><Sub>Deducted once</Sub></Fact>
          <Fact><Label>FX set</Label><Value>{model.authorityBundle.fxRatesUsed ? 'Frozen' : 'Single currency'}</Value><Sub>{ccy} facility currency</Sub></Fact>
          <Fact><Label>Input fingerprint</Label><Value>{shortHash(model.authorityBundle.inputFingerprint)}</Value><Sub>{display(model.authorityBundle.calculationVersion)}</Sub></Fact>
          <Fact><Label>Output fingerprint</Label><Value>{shortHash(model.authorityBundle.outputFingerprint)}</Value><Sub>Immutable calculation evidence</Sub></Fact>
          <Fact><Label>Borrower BBC evidence</Label><Value>{display(model.authorityBundle.reportedBbc.source_filename || model.authorityBundle.reportedBbc.source_reference)}</Value><Sub>{date(model.authorityBundle.reportedBbc.as_of_date)}</Sub></Fact>
        </FactGrid>
        {model.authorityBundle.findings.map(finding => <Finding key={finding}>{finding}</Finding>)}
      </Panel>

      <TwoColumn>
        <Panel>
          <Heading><div><h3>Calculation bridge <ExplainMetricButton scope="selection" metric="BORROWING_BASE" /></h3><p>Persisted engine output; no borrowing-base logic is recomputed in the browser.</p></div><Calculator size={18} color="#38bdf8" /></Heading>
          <Table>
            <Row $header><span>Step</span><span>Amount</span><span>Rows</span><span>Authority</span></Row>
            {model.calculationBridge.map(line => (
              <Row key={line.key}>
                <span>{line.label}<Explanation>{line.explanation}</Explanation>{(() => {
                  const target = bridgeAction(line.key);
                  return target ? <EvidenceLink type="button" onClick={target.action}>{target.label} <ExternalLink size={11} /></EvidenceLink> : null;
                })()}</span>
                <span>{signedMoney(line.amount, ccy)}</span>
                <span>{line.count == null ? '—' : line.count.toLocaleString()}</span>
                <Status $tone={line.key === 'COMPUTED_BB' ? 'green' : 'amber'}>{line.key === 'COMPUTED_BB' ? 'Computed' : 'Input'}</Status>
              </Row>
            ))}
          </Table>
        </Panel>
        <Panel id="concentration-evidence">
          <Heading><div><h3>Concentration cuts</h3><p>Bucket-level reductions persisted by the existing engine.</p></div><GitCompareArrows size={18} color="#fbbf24" /></Heading>
          {model.concentrationDetails.length === 0 ? (
            <Empty>No concentration cut was produced for this run. Persisted reduction: {money(run.concentration_cap_reduction || 0, ccy)}.</Empty>
          ) : model.concentrationDetails.map((item, index) => {
            const contributors = Array.isArray(item.contributors) ? item.contributors as Array<Record<string, unknown>> : [];
            return (
              <Fact key={index} style={{ marginBottom: '.45rem' }}>
                <Value>{display(item.type || item.dimension || item.ruleName || `Concentration ${index + 1}`)} · {display(item.bucket)}</Value>
                <Sub>Balance {money(item.balance || item.contributorBalance, ccy)} · cap {display(item.capPct)}% · persisted cut {money(item.excess, ccy)} · {display(item.contributorCount, '0')} contributing assets</Sub>
                {contributors.slice(0, 8).map((contributor, contributorIndex) => (
                  <Contributor key={`${display(contributor.asset_identifier)}-${contributorIndex}`}>
                    <span>{display(contributor.asset_identifier)}<Explanation>{display(contributor.obligor_name || contributor.obligor_identifier)}</Explanation></span>
                    <span>{money(contributor.current_balance, ccy)}</span>
                    <span>{display(contributor.effective_advance_rate_pct)}%</span>
                  </Contributor>
                ))}
                {contributors.length > 8 && <Sub>Showing the eight largest contributors; {contributors.length - 8} additional returned contributors remain in persisted evidence.</Sub>}
              </Fact>
            );
          })}
        </Panel>
      </TwoColumn>

      <Panel id="bbc-comparison">
        <Heading><div><h3>Independent borrower BBC comparison</h3><p>The certificate remains a reported assertion. Only the independently computed run can be approved.</p></div><FileCheck2 size={18} color="#60a5fa" /></Heading>
        {hasCandidate && (
          <>
            <RecordIdentity>
              Comparing <strong>candidate Run #{display(candidate.id)}</strong> · basis {date(candidate.as_of_date)} · created {dateTime(candidate.created_at)} by {display(candidate.created_by)} · Tape #{display(candidate.tape_ingest_id)} · Contract Version {display(activeVersion.version_no)} · Rule Set v{display(currentRuleSet.version_no)}.
            </RecordIdentity>
            <FormGrid>
              <Field>Borrower certificate
                <select value={submissionId} onChange={event => setSubmissionId(event.target.value)}>
                  <option value="">Select same-period certificate</option>
                  {model.bbc.compatibleSubmissions.map(item => (
                    <option key={item.id} value={item.id}>{item.source_filename || item.source_reference} · {date(item.as_of_date)} · {money(item.reported_borrowing_base, ccy)}</option>
                  ))}
                </select>
              </Field>
            </FormGrid>
            <CheckField style={{ marginTop: 12 }}><input type="checkbox" checked={overridePolicy} onChange={event => setOverridePolicy(event.target.checked)} /><span>Override policy thresholds for this comparison</span></CheckField>
            {!overridePolicy && <Sub>The comparison uses the variance policy effective for this period.</Sub>}
            {overridePolicy && <FormGrid>
              <Field>Tolerance<input inputMode="decimal" value={tolerance} onChange={event => setTolerance(event.target.value)} placeholder="Permitted line variance" /></Field>
              <Field>Materiality<input inputMode="decimal" value={materiality} onChange={event => setMateriality(event.target.value)} placeholder="Escalation threshold" /></Field>
              <Field>Blocking threshold<input inputMode="decimal" value={blocking} onChange={event => setBlocking(event.target.value)} placeholder="Approval block threshold" /></Field>
              <Field>Override reason<input value={overrideReason} onChange={event => setOverrideReason(event.target.value)} placeholder="Why this comparison needs different thresholds" /></Field>
            </FormGrid>}
            <ActionRow>
              <PrerequisiteAction
                primary
                disabled={Boolean(busy) || !model.actions.compare.allowed || Boolean(compareExtra)}
                disabledReason={actionReason(model.actions.compare, Boolean(busy), compareExtra)}
                onClick={() => runAction('compare', () => compareBorrowingBaseToBbc(facilityId, {
                  borrowingBaseRunId: Number(candidate.id),
                  reportedSubmissionId: Number(selectedSubmission?.id),
                  ...(overridePolicy ? {
                    toleranceAmount: Number(tolerance), materialityAmount: Number(materiality),
                    blockingAmount: Number(blocking), overrideReason: overrideReason.trim(),
                  } : {}),
                }), 'Independent BBC comparison recorded; approved authority is unchanged.')}
              >
                <GitCompareArrows size={14} /> Compare reported to computed
              </PrerequisiteAction>
            </ActionRow>
          </>
        )}
        {model.bbc.varianceLines.length === 0 ? <Empty>{hasCandidate ? 'No comparison evidence has been recorded for the candidate.' : 'No unapproved candidate is awaiting comparison.'}</Empty> : (
          <Table>
            <Row $header><span>Line</span><span>Reported</span><span>Computed</span><span>Decision</span></Row>
            {model.bbc.varianceLines.map(line => (
              <Row key={line.id}>
                <span>{display(line.line_type)}<Explanation>{line.explanation}</Explanation></span>
                <span>{money(line.reported_value, ccy)}</span>
                <span>{money(line.computed_value, ccy)}</span>
                <Status $tone={line.blocking ? 'red' : line.status === 'MATCHED' ? 'green' : 'amber'}>{display(line.status)}</Status>
              </Row>
            ))}
          </Table>
        )}
      </Panel>

      <TwoColumn>
        <Panel>
          <Heading><div><h3>Prior-run movement bridge</h3><p>Business-date comparison without asking the operator to select internal run IDs.</p></div><GitCompareArrows size={18} color="#a78bfa" /></Heading>
          {model.priorRunBridge.lines.length === 0 ? <Empty>No earlier approved run is available for comparison.</Empty> : (
            <Table>
              <Row $header><span>Metric</span><span>{date(model.priorRunBridge.priorRun.as_of_date)}</span><span>{date(model.priorRunBridge.currentRun.as_of_date)}</span><span>Movement</span></Row>
              {model.priorRunBridge.lines.map(line => (
                <Row key={line.label}><span>{line.label}</span><span>{money(line.prior, ccy)}</span><span>{money(line.current, ccy)}</span><span>{signedMoney(line.delta, ccy)}</span></Row>
              ))}
            </Table>
          )}
        </Panel>
        <Panel id="approval-decision">
          <Heading><div><h3>Approval and evidence</h3><p>Independent decision, supersession, and immutable authority packaging.</p></div><Archive size={18} color="#34d399" /></Heading>
          {hasCandidate && <RecordIdentity>
            Your decision applies only to <strong>Run #{display(candidate.id)}</strong> · {date(candidate.as_of_date)} · {money(candidate.computed_borrowing_base, ccy)} · created {dateTime(candidate.created_at)} by {display(candidate.created_by)}.
          </RecordIdentity>}
          <ControlGrid>
            <Fact><Label>Pending draws</Label><Value>{money(model.approvalControls.pendingDrawAmount, ccy)}</Value><Sub>Facility amount awaiting control</Sub></Fact>
            <Fact><Label>Active reservations</Label><Value>{money(model.approvalControls.activeReservationAmount, ccy)}</Value><Sub>Reserved or released, not yet consumed</Sub></Fact>
            <Fact><Label>Approval exceptions</Label><Value $tone={model.approvalControls.approvalExceptions.some(item => item.blocking) ? 'red' : 'green'}>{model.approvalControls.approvalExceptions.length}</Value><Sub>Facility-wide approval gate</Sub></Fact>
          </ControlGrid>
          {model.approvalControls.reservations.length === 0 ? <Sub>No availability reservations are bound to the approved run.</Sub> : (
            <Table>
              <Row $header><span>Requested</span><span>Amount</span><span>State</span><span>Actor</span></Row>
              {model.approvalControls.reservations.slice(0, 5).map(item => (
                <Row key={String(item.id)}><span>{date(item.requested_date)}</span><span>{money(item.amount, ccy)}</span><Status $tone={item.status === 'CONSUMED' ? 'green' : 'amber'}>{display(item.status)}</Status><span>{display(item.reserved_by)}</span></Row>
              ))}
            </Table>
          )}
          {model.approvalControls.approvalExceptions.length === 0 ? <Sub>No unresolved facility operational exception blocks this borrowing-base approval.</Sub> : (
            <Table style={{ marginTop: '.55rem' }}>
              <Row $header><span>Exception</span><span>Severity</span><span>State</span><span>Decision</span></Row>
              {model.approvalControls.approvalExceptions.map(item => (
                <Row key={String(item.id)}><span>{display(item.title)}<Explanation>{display(item.description)}</Explanation></span><span>{display(item.severity)}</span><Status $tone={item.blocking ? 'red' : 'amber'}>{display(item.status)}</Status><span>{item.blocking ? 'Blocking' : 'Non-blocking'}</span></Row>
              ))}
            </Table>
          )}
          {acceptedVarianceResolutions.map(item => (
            <RecordIdentity key={String(item.id)} style={{ marginTop: '.7rem', borderColor: 'rgba(52,211,153,.3)', background: 'rgba(6,78,59,.16)', color: '#a7f3d0' }}>
              <strong>Blocking BBC variance accepted under four-eyes review.</strong><br/>
              Exception #{display(item.id)} was proposed by {display(item.proposed_by)} and independently approved by {display(item.reviewed_by)} on {dateTime(item.reviewed_at)}.
              The original `BLOCKING VARIANCE` comparison remains immutable evidence; this accepted disposition permits the separate Run #{display(candidate.id)} authority decision.
            </RecordIdentity>
          ))}
          {hasCandidate && <Sub style={{ marginTop: '.7rem' }}>Run #{display(candidate.id)} is decided in Period review, where the month's open items are in view. This screen is the authority record.</Sub>}
          <ActionRow>
            {hasCandidate && <PrerequisiteAction primary onClick={openPeriodReview}><CheckCircle2 size={14} /> Decide in Period review</PrerequisiteAction>}
            <PrerequisiteAction
              disabled={Boolean(busy) || !model.actions.evidencePack.allowed}
              disabledReason={actionReason(model.actions.evidencePack, Boolean(busy))}
              onClick={() => runAction('evidence', () => finalizeBorrowingBaseEvidencePack(facilityId, Number(approved.id), String(approved.as_of_date)), `${date(approved.as_of_date)} borrowing-base authority evidence pack finalized for ${facilityName}.`)}
            ><Archive size={14} /> Finalize evidence pack</PrerequisiteAction>
          </ActionRow>
          {!model.actions.evidencePack.allowed && <InlineReason>Evidence pack is unavailable: {model.actions.evidencePack.disabledReason}</InlineReason>}
          {model.evidencePacks.length > 0 && (
            <>
              <Sub style={{ margin: '.85rem 0 .4rem' }}>Persisted evidence packs</Sub>
              <Table>
                <Row $header><span>Basis</span><span>Pack</span><span>Status</span><span>Finalized by</span></Row>
                {model.evidencePacks.slice(0, 3).map(pack => (
                  <Row key={String(pack.id)}>
                    <span>{date(pack.as_of_date)}<Explanation>{shortHash(pack.manifest_hash)}</Explanation></span>
                    <span>{display(pack.pack_type)}</span>
                    <Status $tone={pack.status === 'FINAL' || pack.status === 'FINALIZED' ? 'green' : 'amber'}>{display(pack.status)}</Status>
                    <span>{display(pack.finalized_by)}<Explanation>{date(pack.finalized_at || pack.created_at)}</Explanation></span>
                  </Row>
                ))}
              </Table>
            </>
          )}
          <Sub style={{ marginTop: '.7rem' }}>{model.authorityBoundary}</Sub>
        </Panel>
      </TwoColumn>

      <Panel id="approval-lineage">
        <Heading><div><h3>Approval and supersession history</h3><p>Business dates, states, actors and frozen calculation lineage.</p></div><Archive size={18} color="#94a3b8" /></Heading>
        <Table>
          <Row $header><span>Basis</span><span>Amount</span><span>State</span><span>Actor</span></Row>
          {model.history.map(item => (
            <Row key={item.id}>
              <span><strong>Run #{display(item.id)}</strong> · {date(item.as_of_date)}<Explanation>Tape #{display(item.tape_ingest_id, '—')} · Contract v{display(item.facility_version_no, '—')} · Rule set v{display(item.rule_set_version_no, '—')} (ID {display(item.rule_set_id, '—')}) · {display(item.calculation_version)}</Explanation>{item.approval_notes && <Explanation>{item.approval_notes}</Explanation>}</span>
              <span>{money(item.computed_borrowing_base, ccy)}</span>
              <Status $tone={item.approval_status === 'APPROVED' ? 'green' : item.approval_status === 'REJECTED' ? 'red' : 'amber'}>{display(item.approval_status)}</Status>
              <span>{display(item.approved_by || item.created_by)}<Explanation>{item.approved_at ? `Decided ${dateTime(item.approved_at)}` : `Created ${dateTime(item.created_at)}`}</Explanation></span>
            </Row>
          ))}
        </Table>
      </Panel>
    </Shell>
  );
};

export default BorrowingBaseWorkspace;
